import React from 'react';
import { useIsMobile } from '../ui/use-mobile';
import { bookingBrandFontFamilyVar, teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';

export interface TeeSheetPlayerCellTeeTime {
  id: string;
  dateTime: Date;
  memberPlayers?: Array<{
    spotNumber: number;
    firstName: string;
    lastName: string;
    customerType: string;
  }>;
}

export interface TeeSheetPlayerBrandColors {
  fontFamily: string;
  textColor: string;
  secondaryColor: string;
}

export const TeeSheetPlayerCell: React.FC<{
  playerIndex: number;
  teeTime: TeeSheetPlayerCellTeeTime;
  isExpandedView: boolean;
  onCellClick: (e: React.MouseEvent) => void;
  isBlocked?: boolean;
  brandColors?: TeeSheetPlayerBrandColors;
}> = ({
  playerIndex,
  teeTime,
  isExpandedView,
  onCellClick,
  isBlocked = false,
  brandColors: _unusedBrandColors,
}) => {
  const tebox = useBookingTebox();
  const isMobile = useIsMobile();
  const memberPlayer = teeTime.memberPlayers?.find((mp) => mp.spotNumber === playerIndex);
  const borderSubtle = teboxDesignSystem.colors.border.light;
  const radius = `var(--brand-border-radius, ${teboxDesignSystem.borderRadius.md})`;

  if (isBlocked) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px',
          borderRadius: radius,
          backgroundColor: tebox.colors.bgSecondary,
          border: `1px solid ${borderSubtle}`,
          minHeight: isExpandedView ? '48px' : '24px',
          height: isExpandedView ? '48px' : '24px',
          fontSize: teboxDesignSystem.typography.caption.fontSize,
          fontWeight: 500,
          color: tebox.colors.textSecondary,
          fontFamily: bookingBrandFontFamilyVar,
          cursor: 'default',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box',
          lineHeight: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            position: 'relative',
            height: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              marginLeft: '8px',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: isExpandedView
                  ? teboxDesignSystem.typography.caption.fontSize
                  : teboxDesignSystem.typography.body.fontSize,
              }}
            >
              Blocked
            </span>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '32px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              backgroundColor: 'transparent',
              borderRadius: `var(--brand-border-radius, ${teboxDesignSystem.borderRadius.sm})`,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 8H17V6C17 3.24 14.76 1 12 1S7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15S10.9 13 12 13S14 13.9 14 15S13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9S15.1 4.29 15.1 6V8Z"
                fill={tebox.colors.textSecondary}
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  if (memberPlayer) {
    const isGuestPlayer =
      memberPlayer.customerType === 'Guest' ||
      (memberPlayer.firstName === 'Guest' && !memberPlayer.lastName);
    const isPrivacyEnabled =
      memberPlayer.firstName === 'Member' &&
      !memberPlayer.lastName &&
      memberPlayer.customerType !== 'Guest';
    const typeOnlyPlaceholder =
      !!memberPlayer.customerType &&
      !memberPlayer.lastName &&
      !!memberPlayer.firstName &&
      memberPlayer.firstName.trim() === memberPlayer.customerType.trim();

    let displayName: string;
    if (isGuestPlayer) displayName = 'Guest';
    else if (isPrivacyEnabled) displayName = 'Member';
    else if (typeOnlyPlaceholder) displayName = 'Guest';
    else {
      displayName =
        [memberPlayer.firstName, memberPlayer.lastName]
          .map((s) => (s ? s.trim() : ''))
          .filter(Boolean)
          .join(' ')
          .trim() || 'Guest';
    }

    const captionStrong = teboxDesignSystem.typography.caption.fontSize;

    const useTwoLineName =
      isMobile &&
      displayName !== 'Guest' &&
      displayName !== 'Member' &&
      !!memberPlayer.firstName?.trim() &&
      !!memberPlayer.lastName?.trim();

    let nameMarkup: React.ReactNode;
    if (useTwoLineName) {
      nameMarkup = (
        <>
          <span
            style={{
              fontSize: captionStrong,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              lineHeight: 1.2,
            }}
          >
            {memberPlayer.firstName.trim()}
          </span>
          <span
            style={{
              fontSize: captionStrong,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              lineHeight: 1.2,
            }}
          >
            {memberPlayer.lastName.trim()}
          </span>
        </>
      );
    } else {
      nameMarkup = (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {displayName}
        </span>
      );
    }

    return (
      <div
        onClick={onCellClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px',
          borderRadius: radius,
          backgroundColor: teboxDesignSystem.colors.background.primary,
          border: `1px solid ${borderSubtle}`,
          minHeight: isExpandedView ? '48px' : '24px',
          height: isExpandedView ? '48px' : '24px',
          fontSize: teboxDesignSystem.typography.body.fontSize,
          fontWeight: 500,
          color: tebox.colors.textPrimary,
          fontFamily: bookingBrandFontFamilyVar,
          cursor: 'pointer',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = tebox.colors.bgHighlight;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = teboxDesignSystem.colors.background.primary;
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            position: 'relative',
            height: '100%',
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: useTwoLineName ? 'column' : 'row',
              alignItems: useTwoLineName ? 'flex-start' : 'center',
              justifyContent: 'flex-start',
              marginLeft: '8px',
              marginRight: '8px',
              overflow: 'hidden',
              minWidth: 0,
              flex: 1,
              gap: useTwoLineName ? '2px' : '4px',
            }}
          >
            {nameMarkup}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onCellClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
        borderRadius: radius,
        backgroundColor: tebox.colors.bgPrimary,
        border: `1px solid ${borderSubtle}`,
        minHeight: isExpandedView ? '48px' : '24px',
        height: isExpandedView ? '48px' : '24px',
        fontSize: teboxDesignSystem.typography.body.fontSize,
        fontWeight: 500,
        color: tebox.colors.textSecondary,
        fontFamily: bookingBrandFontFamilyVar,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
        lineHeight: 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
        e.currentTarget.style.borderColor = teboxDesignSystem.colors.border.dark;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = tebox.colors.bgPrimary;
        e.currentTarget.style.borderColor = borderSubtle;
      }}
    />
  );
};
